// Email signup: background submit for every signup form, plus the delayed pop-up
// and the small tab. Settings come from data attributes set in the theme editor.
(() => {
  const DAY = 24 * 60 * 60 * 1000;
  const KEY = {
    signedUp: 'gs-signup-done',
    dismissed: 'gs-popup-dismissed',
    tabHidden: 'gs-tab-hidden',
    shown: 'gs-popup-shown',
    pages: 'gs-pages-viewed',
    siteStart: 'gs-site-start',
    fromEmail: 'gs-from-email',
  };

  // Storage can throw (private mode, blocked site data); everything still works without it
  const store = (area) => ({
    get: (key) => {
      try {
        return window[area].getItem(key);
      } catch (error) {
        return null;
      }
    },
    set: (key, value) => {
      try {
        window[area].setItem(key, value);
      } catch (error) {
        /* ignore */
      }
    },
  });
  const local = store('localStorage');
  const session = store('sessionStorage');

  const params = new URLSearchParams(window.location.search);
  if (params.get('customer_posted') === 'true') local.set(KEY.signedUp, '1');
  if (params.get('utm_medium') === 'email') session.set(KEY.fromEmail, '1');

  const pagesViewed = Number(session.get(KEY.pages) || 0) + 1;
  session.set(KEY.pages, String(pagesViewed));
  if (!session.get(KEY.siteStart)) session.set(KEY.siteStart, String(Date.now()));
  const pageStart = Date.now();

  const popup = document.querySelector('[data-gs-popup]');
  const tab = document.querySelector('[data-gs-tab]');
  const isSignedUp = () => local.get(KEY.signedUp) === '1';

  /* ---------- Forms ---------- */

  const showStatus = (form, message, isError) => {
    const status = form.querySelector('[data-gs-signup-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('gs-signup__message--error', isError);
    status.hidden = false;
    const email = form.querySelector('input[type="email"]');
    if (email) email.setAttribute('aria-invalid', String(isError));
  };

  const showSuccess = (form, subscribed = true) => {
    const success = document.createElement('p');
    success.className = 'gs-signup__message gs-signup__message--success';
    success.setAttribute('role', 'status');
    success.setAttribute('tabindex', '-1');
    success.textContent = form.dataset.gsSuccess || window.gsSignupStrings?.success || '';
    [...form.children].forEach((child) => {
      if (child.type !== 'hidden') child.remove();
    });
    form.appendChild(success);
    success.focus({ preventScroll: true });

    // A restock request without the marketing box ticked is not a signup
    if (!subscribed) return;
    local.set(KEY.signedUp, '1');
    if (tab) tab.hidden = true;
    if (popup) popup.classList.add('gs-popup--joined');
  };

  // Back-in-stock forms: unticked, the request goes out as a contact message (nobody
  // is subscribed); ticked, as a customer signup. Switched on change rather than on
  // submit, because Shopify's spam check can submit the form without a submit event.
  const syncNotify = (form) => {
    const marketing = form.querySelector('[data-gs-notify-marketing]');
    const type = form.querySelector('[name="form_type"]');
    if (!marketing || !type) return;
    type.value = marketing.checked ? 'customer' : 'contact';
    form.querySelectorAll('[data-gs-notify-customer]').forEach((input) => {
      input.disabled = !marketing.checked;
    });
    form.querySelectorAll('[data-gs-notify-contact]').forEach((input) => {
      input.disabled = marketing.checked;
    });
  };

  document.querySelectorAll('form[data-gs-signup="notify"]').forEach(syncNotify);
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-gs-notify-marketing]')) syncNotify(event.target.form);
  });

  // A back-in-stock request that finished with a page reload: show its success message
  const posted = params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true';
  const postedForm = posted && window.location.hash ? document.getElementById(window.location.hash.slice(1)) : null;
  if (postedForm && postedForm.matches('form[data-gs-signup="notify"]')) {
    showSuccess(postedForm, params.get('customer_posted') === 'true');
    postedForm.scrollIntoView({ block: 'center' });
  }

  // A normal submit, used whenever the background request can't be trusted
  const submitNormally = (form) => {
    form.dataset.gsNative = 'true';
    HTMLFormElement.prototype.submit.call(form);
  };

  // Shopify's spam protection holds the first submit, adds a CAPTCHA token, then
  // submits again. Stay out of the way until the token is there.
  const waitingForCaptcha = (form) => {
    const protectedForm = form.dataset.cptcha || form.dataset.hcaptchaBound || form.dataset.recaptchaBound;
    const token = form.querySelector(
      '[name="h-captcha-response"], [name="g-recaptcha-response"], [name="recaptcha-v3-token"]'
    );
    return !!protectedForm && !(token && token.value);
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-gs-signup]');
    if (!form || form.dataset.gsNative || event.defaultPrevented || waitingForCaptcha(form)) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
      });
      const url = response.url || '';

      // Shopify asks some visitors to prove they're human: hand over to a normal submit
      if (url.includes('/challenge') || !response.ok) return submitNormally(form);

      if (url.includes('customer_posted=true')) return showSuccess(form);
      if (url.includes('contact_posted=true')) return showSuccess(form, false);

      // Shopify re-renders the page with the error inside this same form
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const error = page.querySelector(`#${CSS.escape(form.id)} [data-gs-signup-status]`);
      const message = error && error.textContent.trim();
      if (!message) return submitNormally(form);
      showStatus(form, message, true);
    } catch (error) {
      submitNormally(form);
    } finally {
      if (button) button.disabled = false;
    }
  });

  /* ---------- Pop-up and tab ---------- */

  if (!popup) return;

  // A pop-up signup that finished with a page reload: reopen it on its success message
  if (params.get('customer_posted') === 'true' && window.location.hash === '#GsPopupSignup') {
    popup.classList.add('gs-popup--joined');
    popup.hidden = false;
    popup.querySelectorAll('[data-gs-popup-close]').forEach((el) =>
      el.addEventListener('click', () => {
        popup.hidden = true;
      })
    );
    return;
  }

  if (isSignedUp()) return;

  const settings = {
    secondPageDelay: Number(popup.dataset.secondPageDelay) * 1000,
    scroll: Number(popup.dataset.scroll) / 100,
    firstPageDelay: Number(popup.dataset.firstPageDelay) * 1000,
    exitDelay: Number(popup.dataset.exitDelay) * 1000,
    dismissFor: Number(popup.dataset.dismissDays) * DAY,
  };
  const panel = popup.querySelector('[role="dialog"]');
  const tags = popup.querySelector('[data-gs-signup-tags]');
  const desktop = window.matchMedia('(min-width: 750px)');
  let lastFocus = null;

  const within = (key) => Number(local.get(key) || 0) > Date.now() - settings.dismissFor;

  const showTab = () => {
    if (tab && !isSignedUp() && !within(KEY.tabHidden)) tab.hidden = false;
  };

  const focusable = () =>
    [...panel.querySelectorAll('button, input, a[href], [tabindex]:not([tabindex="-1"])')].filter(
      (el) => !el.disabled && el.offsetParent !== null
    );

  const trapFocus = (event) => {
    if (event.key === 'Escape') return close();
    if (event.key !== 'Tab' || !desktop.matches) return;
    const items = focusable();
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const open = (source) => {
    if (!popup.hidden) return;
    lastFocus = document.activeElement;
    if (tags) tags.value = `newsletter, src-${source}`;
    panel.setAttribute('aria-modal', String(desktop.matches));
    popup.hidden = false;
    if (tab) tab.hidden = true;
    session.set(KEY.shown, '1');
    panel.focus({ preventScroll: true });
    document.addEventListener('keydown', trapFocus);
  };

  function close() {
    if (popup.hidden) return;
    popup.hidden = true;
    document.removeEventListener('keydown', trapFocus);
    if (!isSignedUp()) {
      local.set(KEY.dismissed, String(Date.now()));
      showTab();
    }
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  popup.querySelectorAll('[data-gs-popup-close]').forEach((el) => el.addEventListener('click', close));

  if (tab) {
    tab.querySelector('[data-gs-tab-open]').addEventListener('click', () => open('tab'));
    tab.querySelector('[data-gs-tab-close]').addEventListener('click', () => {
      local.set(KEY.tabHidden, String(Date.now()));
      tab.hidden = true;
    });
  }

  if (within(KEY.dismissed)) {
    showTab();
    return;
  }
  if (session.get(KEY.shown) || session.get(KEY.fromEmail)) return;

  // Never interrupt the cart notification or someone typing
  const busy = () => {
    if (document.querySelector('#cart-notification.active, cart-drawer.active')) return true;
    const active = document.activeElement;
    return !!active && (active.matches('input, textarea, select') || active.isContentEditable);
  };

  let opened = false;
  const tryOpen = () => {
    if (opened || isSignedUp()) return;
    if (busy()) return void setTimeout(tryOpen, 1000);
    opened = true;
    open('popup');
  };

  if (pagesViewed >= 2) {
    setTimeout(tryOpen, settings.secondPageDelay);
  } else {
    const scrolledEnough = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      return scrollable <= 0 || window.scrollY / scrollable >= settings.scroll;
    };
    const check = () => {
      if (Date.now() - pageStart >= settings.firstPageDelay && scrolledEnough()) tryOpen();
    };
    window.addEventListener('scroll', check, { passive: true });
    setTimeout(check, settings.firstPageDelay);
  }

  // Desktop only: the cursor leaving through the top of the window
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('mouseout', (event) => {
      if (event.relatedTarget || event.clientY > 0 || !desktop.matches) return;
      const onSite = Date.now() - Number(session.get(KEY.siteStart) || pageStart);
      if (onSite >= settings.exitDelay) tryOpen();
    });
  }
})();
